import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

const buttonVariants = cva(
  // Base recipe shared by every variant. Matches the marketing site's
  // inline button spec so app/marketing/email all read as the same
  // brand component (`bg-brand-sage` text-cream `shadow-sm rounded-md`
  // text-sm font-medium). See packages/notifications/src/email-templates.ts
  // for the email equivalent.
  // `whitespace-nowrap` stops word-wrapping. `[&_svg]:shrink-0` stops
  // icons from collapsing inside a tight container. The inner children
  // wrapper below uses inline-flex so an icon passed as a *child*
  // (e.g. <Button><Icon /> Send</Button>) lays out beside its text
  // instead of stacking — Tailwind preflight sets `svg { display:
  // block }` which would otherwise put the icon on its own line.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary CTA — matches the marketing "Start free trial"
        // design direction: sage-green fill, cream text, *subtle*
        // shadow-sm and font-medium (not the deep, semibold,
        // heavily-drop-shadowed variant we briefly used after the
        // marketing/app button consolidation). One definition for the
        // whole product; tweaks here ripple through marketing + app.
        primary:
          'bg-brand-sage font-medium text-brand-cream shadow-sm hover:bg-brand-sage/90 active:bg-brand-sage/85',
        destructive:
          'bg-destructive font-medium text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/85',
        ghost: 'text-secondary-foreground hover:bg-accent',
        link: 'text-primary underline-offset-4 hover:underline px-0 py-0',
        // Mid-emphasis. Used for Cancel-adjacent or "alternative
        // path" actions where a ghost would be too quiet but a
        // primary would steal focus from the real CTA.
        outline:
          'border border-brand-sage/30 bg-card text-brand-ink-soft hover:bg-brand-cream-shadow',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;
export type ButtonVariant = NonNullable<ButtonVariantProps['variant']>;
export type ButtonSize = NonNullable<ButtonVariantProps['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariantProps {
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

function Button({
  variant,
  size,
  asChild = false,
  loading,
  leadingIcon,
  trailingIcon,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  if (asChild) {
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        buttonVariants({ variant, size: variant === 'link' ? undefined : size, className }),
      )}
    >
      {loading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden
        />
      ) : leadingIcon ? (
        <span aria-hidden>{leadingIcon}</span>
      ) : null}
      <span className="inline-flex items-center gap-1.5">{children}</span>
      {trailingIcon && !loading ? <span aria-hidden>{trailingIcon}</span> : null}
    </button>
  );
}

export { Button, buttonVariants };
